
import { ethers } from "ethers";
import { Dispatch, SetStateAction, useRef } from "react";
import useSWR from "swr";

import OrderBook from "sdk/abis/OrderBook.json";
import PositionManager from "sdk/abis/PositionManager.json";
import PositionRouter from "sdk/abis/PositionRouter.json";
import Router from "sdk/abis/Router.json";
import Token from "sdk/abis/Token.json";
import Vault from "sdk/abis/Vault.json";

import { ARBITRUM, BSC, BESC, AVALANCHE_FUJI, getChainName, getConstant, getHighExecutionFee } from "config/chains";
import { getContract, getV1VaultAddress } from "config/contracts";
import { DECREASE, INCREASE, SWAP, getOrderKey } from "lib/legacy";
import { USD_DECIMALS } from "config/factors";

import { t } from "@lingui/macro";
import { getServerBaseUrl } from "config/backend";
import { callContract, contractFetcher } from "lib/contracts";
import { BN_ZERO, bigNumberify, expandDecimals } from "lib/numbers";
import { getProvider, useJsonRpcProvider } from "lib/rpc";
import { fetchTrades, fetchSwaps, fetchUserStats, fetchOrders, fetchVolumeStats, fetchLivePrices } from "lib/indexer/client";
import groupBy from "lodash/groupBy";
import { replaceNativeTokenAddress } from "./tokens";
import { getUsd } from "./tokens/utils";
import { getToken } from "config/tokens";
import useWallet from "lib/wallets/useWallet";
import useSWRInfinite from "swr/infinite";
import { OrderMetricId } from "lib/metrics";

export * from "./prices";

export type PendingTransaction = {
  hash: string;
  message: string;
  messageDetails?: string;
  metricId?: OrderMetricId;
};

export type SetPendingTransactions = Dispatch<SetStateAction<PendingTransaction[]>>;

const { ZeroAddress } = ethers;

export function useAllOrdersStats(_chainId) {
  return null;
}

export function useUserStat(chainId) {
  const { data } = useSWR(["useUserStat", chainId], async () => {
    const res = await fetchUserStats();
    return { id: "total", uniqueCount: res.totalUsers };
  });
  return data ?? null;
}

export function useLiquidationsData(chainId, account) {
  const { data } = useSWR(
    account ? ["liquidationsData", chainId, account] : null,
    async () => {
      const res = await fetchTrades({ account: account.toLowerCase(), action: "liquidate", pageSize: 100 });
      return res.items.map((row) => ({
        key: row.tx_hash,
        timestamp: row.block_ts,
        borrowFee: bigNumberify(row.fee) ?? 0n,
        loss:
          row.realised_pnl
            ? BigInt(row.realised_pnl) < 0n
              ? -BigInt(row.realised_pnl)
              : BigInt(row.realised_pnl)
            : 0n,
        collateral: bigNumberify(row.collateral_delta) ?? 0n,
        size: bigNumberify(row.size_delta) ?? 0n,
        markPrice: bigNumberify(row.price) ?? 0n,
        type: "full",
      }));
    }
  );
  return data ?? null;
}

export function useAllPositions(_chainId, _signer) {
  return [];
}

export function useAllOrders(_chainId, _signer) {
  return [];
}

export function useAllOrdersByAccount(chainId, signer, account) {
  // Source the order list straight from the OrderBook on-chain — the indexer's
  // order ingester is unreliable, so we read the per-account counters and fan
  // out over every index (details are fetched below, cancelled ones skipped).
  const { data: orderItems = [] } = useSWR(
    account ? ["allOrderIndices", chainId, account] : null,
    async () => {
      const provider = getProvider(signer, chainId);
      const orderBookAddress = getContract(chainId, "OrderBook");
      const contract = new ethers.Contract(orderBookAddress, OrderBook.abi, provider);
      const [incCount, decCount, swapCount] = await Promise.all([
        contract.increaseOrdersIndex(account).catch(() => 0n),
        contract.decreaseOrdersIndex(account).catch(() => 0n),
        contract.swapOrdersIndex(account).catch(() => 0n),
      ]);
      const items: any[] = [];
      for (let i = 0; i < Number(incCount); i++) items.push({ type: "increase", account, index: i });
      for (let i = 0; i < Number(decCount); i++) items.push({ type: "decrease", account, index: i });
      for (let i = 0; i < Number(swapCount); i++) items.push({ type: "swap", account, index: i });
      return items;
    }
  );

  const key = orderItems.length
    ? ["allOrderDetails", chainId, account, orderItems.map((o: any) => `${o.type}-${o.index}`).join(",")]
    : null;

  const { data: orders = [] } = useSWR(key, () => {
    const provider = getProvider(signer, chainId);
    const orderBookAddress = getContract(chainId, "OrderBook");
    const contract = new ethers.Contract(orderBookAddress, OrderBook.abi, provider);
    return Promise.all(
      orderItems.map(async (order: any) => {
        try {
          const type = order.type.charAt(0).toUpperCase() + order.type.substring(1);
          const method = `get${type}Order`;
          const orderFromChain = await contract[method](order.account, order.index);
          // Skip cancelled/executed orders — the mapping entry is deleted, so
          // the index token (or swap path) comes back as the zero address.
          const idxField =
            order.type === "increase" ? orderFromChain[3]
            : order.type === "decrease" ? orderFromChain[2]
            : orderFromChain[0];
          if (!idxField || idxField === ZeroAddress) return undefined;
          const ret: any = {};
          if (order.type === "increase") {
            ret.purchaseToken = orderFromChain[0].toLowerCase();
            ret.purchaseTokenAmount = orderFromChain[1];
            ret.collateralToken = orderFromChain[2].toLowerCase();
            ret.indexToken = orderFromChain[3].toLowerCase();
            ret.sizeDelta = orderFromChain[4];
            ret.isLong = orderFromChain[5];
            ret.triggerPrice = orderFromChain[6];
            ret.triggerAboveThreshold = orderFromChain[7];
            ret.executionFee = orderFromChain[8];
          } else if (order.type === "decrease") {
            ret.collateralToken = orderFromChain[0].toLowerCase();
            ret.collateralDelta = orderFromChain[1];
            ret.indexToken = orderFromChain[2].toLowerCase();
            ret.sizeDelta = orderFromChain[3];
            ret.isLong = orderFromChain[4];
            ret.triggerPrice = orderFromChain[5];
            ret.triggerAboveThreshold = orderFromChain[6];
            ret.executionFee = orderFromChain[7];
          } else if (order.type === "swap") {
            ret.path0 = orderFromChain[0].toLowerCase();
            ret.path1 = orderFromChain[1].toLowerCase();
            ret.path2 = orderFromChain[2].toLowerCase();
            ret.amountIn = orderFromChain[3];
            ret.minOut = orderFromChain[4];
            ret.triggerRatio = orderFromChain[5];
            ret.triggerAboveThreshold = orderFromChain[6];
            ret.shouldUnwrap = orderFromChain[7];
            ret.executionFee = orderFromChain[8];
            ret.path = [ret.path0, ret.path1, ret.path2].filter((address) => address !== ZeroAddress);
          }
          ret.type = type;
          ret.index = order.index;
          ret.account = order.account;
          ret.createdTimestamp = order.createdTimestamp;
          return ret;
        } catch (ex) {
          // eslint-disable-next-line no-console
          console.error(ex);
        }
      })
    );
  });

  return orders.filter(Boolean);
}

export function useAllTradesHistory(chainId, account) {
  const { data: tradeHistoryDataMap } = useSWR(
    account ? ["allTradesHistory", chainId, account] : null,
    async () => {
      const [tradesRes, swapsRes] = await Promise.all([
        fetchTrades({ account: account.toLowerCase(), pageSize: 500 }),
        fetchSwaps({ account: account.toLowerCase(), pageSize: 500 }),
      ]);

      const items: any[] = [];

      for (const row of tradesRes.items) {
        const isLong = !!row.is_long;
        const action =
          row.action === "increase"
            ? isLong
              ? "IncreasePosition-Long"
              : "IncreasePosition-Short"
            : row.action === "decrease"
            ? isLong
              ? "DecreasePosition-Long"
              : "DecreasePosition-Short"
            : isLong
            ? "LiquidatePosition-Long"
            : "LiquidatePosition-Short";

        items.push({
          data: {
            timestamp: row.block_ts,
            txhash: row.tx_hash,
            account: row.account,
            action,
            params: {
              indexToken: ethers.getAddress(row.index_token),
              collateralToken: ethers.getAddress(row.collateral_token),
              isLong,
              sizeDelta: row.size_delta,
              size: row.size_delta,
              price: row.price,
              markPrice: row.price,
              collateralDelta: row.collateral_delta,
              key: row.tx_hash,
            },
          },
        });
      }

      for (const row of swapsRes.items) {
        items.push({
          data: {
            timestamp: row.block_ts,
            txhash: row.tx_hash,
            account: row.account,
            action: "Swap",
            params: {
              tokenIn: ethers.getAddress(row.token_in),
              tokenOut: ethers.getAddress(row.token_out),
              amountIn: row.amount_in,
              amountOut: row.amount_out_after_fees,
            },
          },
        });
      }

      items.sort((a, b) => b.data.timestamp - a.data.timestamp);
      return items;
    }
  );

  return { tradeHistoryDataMap };
}

export function usePositionsForOrders(chainId, signer, orders) {
  const key = orders ? orders.map((order) => getOrderKey(order) + "____") : null;
  const { data: positions = {} } = useSWR(key, async () => {
    const provider = getProvider(signer, chainId);
    const vaultAddress = getV1VaultAddress(chainId);
    const contract = new ethers.Contract(vaultAddress, Vault.abi, provider);
    const data = await Promise.all(
      orders.map(async (order) => {
        try {
          const position = await contract.getPosition(
            order.account,
            order.collateralToken,
            order.indexToken,
            order.isLong
          );
          if (position[0] == 0n) {
            return [null, order];
          }
          return [position, order];
        } catch (ex) {
          // eslint-disable-next-line no-console
          console.error(ex);
        }
      })
    );
    return data.reduce((memo, [position, order]) => {
      memo[getOrderKey(order)] = position;
      return memo;
    }, {});
  });

  return positions;
}

function invariant(condition, errorMsg) {
  if (!condition) {
    throw new Error(errorMsg);
  }
}

export function useTrades(chainId, account) {
  function getFetchUrl(chainId: number, account: string, afterId: string): string {
    const baseUrl = `${getServerBaseUrl(chainId)}/actions`;
    const urlItem = new URL(baseUrl);

    if (account) {
      urlItem.searchParams.append("account", account);
    }

    if (afterId) {
      urlItem.searchParams.append("after", afterId);
    }

    return urlItem.toString();
  }

  function getKey(pageIndex: number, previousPageData) {
    if (previousPageData && !previousPageData.length) return null;
    const afterId = previousPageData && previousPageData[previousPageData.length - 1].id;
    return [getFetchUrl(chainId, account, afterId), pageIndex];
  }

  const {
    data,
    mutate: updateTrades,
    size,
    setSize,
  } = useSWRInfinite(getKey, {
    dedupingInterval: 10000,
    // @ts-ignore
    fetcher: ([url]) => fetch(url).then((res) => res.json()),
  });
  const trades = data ? data.flat() : undefined;

  if (trades) {
    trades.sort((item0, item1) => {
      const data0 = item0.data;
      const data1 = item1.data;
      const time0 = parseInt(data0.timestamp);
      const time1 = parseInt(data1.timestamp);
      if (time1 > time0) {
        return 1;
      }
      if (time1 < time0) {
        return -1;
      }

      const block0 = parseInt(data0.blockNumber);
      const block1 = parseInt(data1.blockNumber);

      if (isNaN(block0) && isNaN(block1)) {
        return 0;
      }

      if (isNaN(block0)) {
        return 1;
      }

      if (isNaN(block1)) {
        return -1;
      }

      if (block1 > block0) {
        return 1;
      }

      if (block1 < block0) {
        return -1;
      }

      return 0;
    });
  }

  return { trades, updateTrades, size, setSize };
}

export function useExecutionFee(active, chainId, infoTokens) {
  const positionRouterAddress = getContract(chainId, "PositionRouter");
  const nativeTokenAddress = getContract(chainId, "NATIVE_TOKEN");
  let { provider } = useJsonRpcProvider(chainId);

  const { data: minExecutionFee } = useSWR<bigint>([active, chainId, positionRouterAddress, "minExecutionFee"], {
    fetcher: contractFetcher(provider, PositionRouter) as any,
  });

  const { data: gasPrice } = useSWR<bigint | undefined>(["gasPrice", chainId], {
    fetcher: () => {
      return new Promise<bigint | undefined>(async (resolve) => {
        if (!provider) {
          // Provider not ready yet (wallet still connecting): use the default
          // RPC provider for this chain. Expected on first render — not an error.
          provider = getProvider(undefined, chainId);
        }

        try {
          const gasPrice = (await provider.getFeeData()).gasPrice;
          resolve(gasPrice ?? undefined);
        } catch (e) {
          // eslint-disable-next-line no-console
          console.error(e);
        }
      });
    },
  });

  let multiplier = 0n;

  if (chainId === ARBITRUM) {
    multiplier = 2150000n;
  }

  // multiplier for Bsc is just the average gas usage
  if (chainId === BSC || chainId === AVALANCHE_FUJI) {
    multiplier = 700000n;
  }

  let finalExecutionFee = minExecutionFee;

  if (gasPrice !== undefined && minExecutionFee !== undefined) {
    const estimatedExecutionFee = gasPrice * multiplier;
    if (estimatedExecutionFee > minExecutionFee) {
      finalExecutionFee = estimatedExecutionFee;
    }
  }

  const finalExecutionFeeUSD = getUsd(finalExecutionFee, nativeTokenAddress, false, infoTokens);
  const isFeeHigh =
    finalExecutionFeeUSD !== undefined &&
    finalExecutionFeeUSD > expandDecimals(getHighExecutionFee(chainId), USD_DECIMALS);
  const errorMessage =
    isFeeHigh &&
    t`The network Fees are very high currently, which may be due to a temporary increase in transactions on the ${getChainName(
      chainId
    )} network.`;

  return {
    minExecutionFee: finalExecutionFee,
    minExecutionFeeUSD: finalExecutionFeeUSD,
    minExecutionFeeErrorMessage: errorMessage,
  };
}

export function useStakedMoneySupply(signer, active) {
  const moneyAddressArb = getContract(ARBITRUM, "MONEY");
  const stakedMoneyTrackerAddressArb = getContract(ARBITRUM, "StakedMoneyTracker");

  const { data: arbData, mutate: arbMutate } = useSWR<any>(
    [`StakeV2:stakedMoneySupply:${active}`, ARBITRUM, moneyAddressArb, "balanceOf", stakedMoneyTrackerAddressArb],
    {
      fetcher: contractFetcher(signer, Token),
    }
  );

  const moneyAddressBsc = getContract(BSC, "MONEY");
  const stakedMoneyTrackerAddressBsc = getContract(BSC, "StakedMoneyTracker");

  const { data: ronData, mutate: ronMutate } = useSWR(
    [`StakeV2:stakedMoneySupply:${active}`, BSC, moneyAddressBsc, "balanceOf", stakedMoneyTrackerAddressBsc],
    {
      fetcher: contractFetcher(undefined, Token),
    }
  );

  let data;
  if (arbData && ronData) {
    data = arbData + ronData;
  }

  const mutate = () => {
    arbMutate();
    ronMutate();
  };

  return { data, mutate };
}

export function useHasOutdatedUi() {
  return { data: false, mutate: () => {} };
}

export function useMoneyPrice(_chainId, _libraries?, _active?) {
  const { data: moneyPriceFromBsc, mutate } = useMoneyPriceFromIndexer();
  return {
    moneyPrice: moneyPriceFromBsc,
    moneyPriceFromArbitrum: undefined,
    moneyPriceFromBsc,
    mutate,
  };
}

export function useTotalMoneySupply() {
  const { data: moneySupply } = useSWR(
    [`StakeV2:totalSupply:${BESC}`, BESC, getContract(BESC, "MONEY"), "totalSupply"],
    { fetcher: contractFetcher(undefined, Token) }
  );
  return { total: moneySupply, mutate: undefined };
}

export function useTotalMoneyStaked() {
  const stakedMoneyTrackerAddress = getContract(BESC, "StakedMoneyTracker");
  let totalStakedMoney = useRef(BN_ZERO);

  const { data: stakedMoneySupply, mutate } = useSWR<bigint>(
    [`StakeV2:stakedMoneySupply:${BESC}`, BESC, getContract(BESC, "MONEY"), "balanceOf", stakedMoneyTrackerAddress],
    { fetcher: contractFetcher(undefined, Token) as any }
  );

  if (stakedMoneySupply !== undefined) {
    totalStakedMoney.current = BigInt(stakedMoneySupply);
  }

  return {
    [BESC]: stakedMoneySupply,
    [BSC]: undefined,
    [ARBITRUM]: undefined,
    total: totalStakedMoney.current,
    mutate,
  };
}

export function useTotalMoneyInLiquidity() {
  // No on-chain LP pool on BESC yet — returns zero until a DEX pool is deployed
  return { total: BN_ZERO, mutate: undefined };
}

function useMoneyPriceFromIndexer() {
  const { data, mutate } = useSWR(
    "moneyPriceFromIndexer",
    async () => {
      try {
        const prices = await fetchLivePrices();
        const moneyUsd = prices["MONEY"];
        if (!moneyUsd) return undefined;
        // Convert USD float to 1e30 bigint (USD_DECIMALS = 30)
        return BigInt(Math.round(moneyUsd * 1e6)) * 10n ** BigInt(USD_DECIMALS - 6);
      } catch {
        return undefined;
      }
    },
    { refreshInterval: 30_000 }
  );
  return { data, mutate };
}

export async function approvePlugin(chainId, pluginAddress, { signer, setPendingTxns, sentMsg, failMsg, router = undefined }: { signer: any; setPendingTxns: any; sentMsg: string; failMsg: string; router?: string }) {
  const routerAddress = router ?? getContract(chainId, "Router");
  const contract = new ethers.Contract(routerAddress, Router.abi, signer);
  return callContract(chainId, contract, "approvePlugin", [pluginAddress], {
    sentMsg,
    failMsg,
    setPendingTxns,
  });
}

export async function createSwapOrder(
  chainId,
  signer,
  path,
  amountIn,
  minOut,
  triggerRatio,
  nativeTokenAddress,
  opts: any = {}
) {
  const executionFee = getConstant(chainId, "SWAP_ORDER_EXECUTION_GAS_FEE");
  const triggerAboveThreshold = false;
  let shouldWrap = false;
  let shouldUnwrap = false;
  opts.value = executionFee;

  if (path[0] === ZeroAddress) {
    shouldWrap = true;
    opts.value = opts.value + amountIn;
  }
  if (path[path.length - 1] === ZeroAddress) {
    shouldUnwrap = true;
  }
  path = replaceNativeTokenAddress(path, nativeTokenAddress);

  const params = [path, amountIn, minOut, triggerRatio, triggerAboveThreshold, executionFee, shouldWrap, shouldUnwrap];

  const orderBookAddress = getContract(chainId, "OrderBook");
  const contract = new ethers.Contract(orderBookAddress, OrderBook.abi, signer);

  return callContract(chainId, contract, "createSwapOrder", params, opts);
}

export async function createIncreaseOrder(
  chainId,
  signer,
  nativeTokenAddress,
  path,
  amountIn,
  indexTokenAddress,
  minOut,
  sizeDelta,
  collateralTokenAddress,
  isLong,
  triggerPrice,
  opts: any = {}
) {
  // Standard GMX requires long collateral == index token. Synthetic markets
  // (BTC/ETH/…) are stable-collateralized (collateral = BUSDC, index = synthetic),
  // so allow the mismatch when the index token is synthetic.
  let indexIsSynthetic = false;
  try {
    indexIsSynthetic = Boolean(getToken(chainId, indexTokenAddress)?.isSynthetic);
  } catch (_) {
    indexIsSynthetic = false;
  }
  invariant(
    !isLong || indexTokenAddress === collateralTokenAddress || indexIsSynthetic,
    "invalid token addresses"
  );
  invariant(indexTokenAddress !== ZeroAddress, "indexToken is 0");
  invariant(collateralTokenAddress !== ZeroAddress, "collateralToken is 0");

  const fromETH = path[0] === ZeroAddress;

  path = replaceNativeTokenAddress(path, nativeTokenAddress);
  const shouldWrap = fromETH;
  const triggerAboveThreshold = !isLong;
  const executionFee = getConstant(chainId, "INCREASE_ORDER_EXECUTION_GAS_FEE");

  const params = [
    path,
    amountIn,
    indexTokenAddress,
    minOut,
    sizeDelta,
    collateralTokenAddress,
    isLong,
    triggerPrice,
    triggerAboveThreshold,
    executionFee,
    shouldWrap,
  ];

  if (!opts.value) {
    opts.value = fromETH ? amountIn + executionFee : executionFee;
  }

  const orderBookAddress = getContract(chainId, "OrderBook");
  const contract = new ethers.Contract(orderBookAddress, OrderBook.abi, signer);

  return callContract(chainId, contract, "createIncreaseOrder", params, opts);
}

export async function createDecreaseOrder(
  chainId,
  signer,
  indexTokenAddress,
  sizeDelta,
  collateralTokenAddress,
  collateralDelta,
  isLong,
  triggerPrice,
  triggerAboveThreshold,
  opts: any = {}
) {
  // Standard GMX requires long collateral == index token. Synthetic markets
  // (BTC/ETH/…) are stable-collateralized (collateral = BUSDC, index = synthetic),
  // so allow the mismatch when the index token is synthetic.
  let indexIsSynthetic = false;
  try {
    indexIsSynthetic = Boolean(getToken(chainId, indexTokenAddress)?.isSynthetic);
  } catch (_) {
    indexIsSynthetic = false;
  }
  invariant(
    !isLong || indexTokenAddress === collateralTokenAddress || indexIsSynthetic,
    "invalid token addresses"
  );
  invariant(indexTokenAddress !== ZeroAddress, "indexToken is 0");
  invariant(collateralTokenAddress !== ZeroAddress, "collateralToken is 0");

  const executionFee = getConstant(chainId, "DECREASE_ORDER_EXECUTION_GAS_FEE");

  const params = [
    indexTokenAddress,
    sizeDelta,
    collateralTokenAddress,
    collateralDelta,
    isLong,
    triggerPrice,
    triggerAboveThreshold,
  ];
  opts.value = executionFee;
  const orderBookAddress = getContract(chainId, "OrderBook");
  const contract = new ethers.Contract(orderBookAddress, OrderBook.abi, signer);

  return callContract(chainId, contract, "createDecreaseOrder", params, opts);
}

export async function createDecreaseOrderWithTrail(
  chainId,
  signer,
  indexTokenAddress,
  sizeDelta,
  collateralTokenAddress,
  collateralDelta,
  isLong,
  triggerPrice,
  triggerAboveThreshold,
  trailAmount, // bigint, in 1e30 units (e.g. $50 trail = 50n * 10n**30n)
  opts: any = {}
) {
  // Standard GMX requires long collateral == index token. Synthetic markets
  // (BTC/ETH/…) are stable-collateralized (collateral = BUSDC, index = synthetic),
  // so allow the mismatch when the index token is synthetic.
  let indexIsSynthetic = false;
  try {
    indexIsSynthetic = Boolean(getToken(chainId, indexTokenAddress)?.isSynthetic);
  } catch (_) {
    indexIsSynthetic = false;
  }
  invariant(
    !isLong || indexTokenAddress === collateralTokenAddress || indexIsSynthetic,
    "invalid token addresses"
  );
  invariant(indexTokenAddress !== ZeroAddress, "indexToken is 0");
  invariant(collateralTokenAddress !== ZeroAddress, "collateralToken is 0");

  const executionFee = getConstant(chainId, "DECREASE_ORDER_EXECUTION_GAS_FEE");

  const params = [
    indexTokenAddress,
    sizeDelta,
    collateralTokenAddress,
    collateralDelta,
    isLong,
    triggerPrice,
    triggerAboveThreshold,
    trailAmount,
  ];
  opts.value = executionFee;
  const orderBookAddress = getContract(chainId, "OrderBook");
  const contract = new ethers.Contract(orderBookAddress, OrderBook.abi, signer);

  return callContract(chainId, contract, "createDecreaseOrderWithTrail", params, opts);
}

/**
 * closeAllPositions — market-close every open position in one call-chain.
 *
 * Works for both the regular vault (PositionRouter) and the synthetic vault
 * (SynthPositionRouter, if configured on the chain).  Each position is closed
 * with a separate `createDecreasePosition` call because PositionRouter does not
 * have a multi-close batch method.
 *
 * Positions are identified by their on-chain key fields: indexToken,
 * collateralToken, isLong.  acceptablePrice is set to 0 (long) or MaxUint256
 * (short) so the tx fills at any price — same as a regular market close.
 *
 * @param positions  Array of open position objects (from usePositions / Exchange).
 *                   Must have: { indexToken, collateralToken, isLong, size, isSynth? }
 */
export async function closeAllPositions(
  chainId: number,
  signer: any,
  account: string,
  positions: Array<{
    indexToken: { address: string; isNative?: boolean };
    collateralToken: { address: string };
    isLong: boolean;
    size: bigint;
    isSynth?: boolean;
  }>,
  executionFee: bigint,
  opts: any = {}
) {
  const { tryGetContract } = await import("config/contracts");
  const prAddr     = getContract(chainId, "PositionRouter");
  const synthPrAddr = tryGetContract(chainId, "SynthPositionRouter") ?? prAddr;
  const { ZeroAddress, MaxUint256 } = ethers;

  const txns: Promise<any>[] = [];

  for (const pos of positions) {
    const routerAddr = pos.isSynth ? synthPrAddr : prAddr;
    const contract   = new ethers.Contract(routerAddr, PositionRouter.abi, signer);

    const path = [pos.collateralToken.address];
    const acceptablePrice = pos.isLong ? 0n : MaxUint256;

    const params = [{
      _path:             path,
      _indexToken:       pos.indexToken.address,
      _collateralDelta:  0n,         // withdraw all remaining collateral
      _sizeDelta:        pos.size,
      _isLong:           pos.isLong,
      _receiver:         account,
      _acceptablePrice:  acceptablePrice,
      _minOut:           0n,
      _executionFee:     executionFee,
      _withdrawETH:      pos.collateralToken.address === ZeroAddress,
      _callbackTarget:   ZeroAddress,
    }];

    txns.push(
      callContract(chainId, contract, "createDecreasePosition", params, {
        ...opts,
        value: executionFee,
        // Don't show individual success toasts — caller handles the summary
        hideSentMsg: true,
        hideSuccessMsg: true,
      }).catch(() => null) // continue even if one fails
    );
  }

  await Promise.all(txns);
}

export async function cancelSwapOrder(chainId, signer, index, opts) {
  const params = [index];
  const method = "cancelSwapOrder";
  const orderBookAddress = getContract(chainId, "OrderBook");
  const contract = new ethers.Contract(orderBookAddress, OrderBook.abi, signer);

  return callContract(chainId, contract, method, params, opts);
}

export async function cancelDecreaseOrder(chainId, signer, index, opts) {
  const params = [index];
  const method = "cancelDecreaseOrder";
  const orderBookAddress = getContract(chainId, "OrderBook");
  const contract = new ethers.Contract(orderBookAddress, OrderBook.abi, signer);

  return callContract(chainId, contract, method, params, opts);
}

export async function cancelIncreaseOrder(chainId, signer, index, opts) {
  const params = [index];
  const method = "cancelIncreaseOrder";
  const orderBookAddress = getContract(chainId, "OrderBook");
  const contract = new ethers.Contract(orderBookAddress, OrderBook.abi, signer);

  return callContract(chainId, contract, method, params, opts);
}

export function handleCancelOrder(chainId, signer, order, opts) {
  let func;
  if (order.type === SWAP) {
    func = cancelSwapOrder;
  } else if (order.type === INCREASE) {
    func = cancelIncreaseOrder;
  } else if (order.type === DECREASE) {
    func = cancelDecreaseOrder;
  }

  return func(chainId, signer, order.index, {
    successMsg: t`Order cancelled.`,
    failMsg: t`Cancel failed.`,
    sentMsg: t`Cancel submitted.`,
    pendingTxns: opts.pendingTxns,
    setPendingTxns: opts.setPendingTxns,
  });
}

export async function cancelMultipleOrders(chainId, signer, allIndexes: any[] = [], opts) {
  const ordersWithTypes = groupBy(allIndexes, (v) => v.split("-")[0]);
  function getIndexes(key) {
    if (!ordersWithTypes[key]) return;
    return ordersWithTypes[key].map((d) => d.split("-")[1]);
  }
  // params order => swap, increase, decrease
  const params = ["Swap", "Increase", "Decrease"].map((key) => getIndexes(key) || []);
  const method = "cancelMultiple";
  const orderBookAddress = getContract(chainId, "OrderBook");
  const contract = new ethers.Contract(orderBookAddress, OrderBook.abi, signer);
  return callContract(chainId, contract, method, params, opts);
}

export async function updateDecreaseOrder(
  chainId,
  signer,
  index,
  collateralDelta,
  sizeDelta,
  triggerPrice,
  triggerAboveThreshold,
  opts
) {
  const params = [index, collateralDelta, sizeDelta, triggerPrice, triggerAboveThreshold];
  const method = "updateDecreaseOrder";
  const orderBookAddress = getContract(chainId, "OrderBook");
  const contract = new ethers.Contract(orderBookAddress, OrderBook.abi, signer);

  return callContract(chainId, contract, method, params, opts);
}

export async function updateIncreaseOrder(
  chainId,
  signer,
  index,
  sizeDelta,
  triggerPrice,
  triggerAboveThreshold,
  opts
) {
  const params = [index, sizeDelta, triggerPrice, triggerAboveThreshold];
  const method = "updateIncreaseOrder";
  const orderBookAddress = getContract(chainId, "OrderBook");
  const contract = new ethers.Contract(orderBookAddress, OrderBook.abi, signer);

  return callContract(chainId, contract, method, params, opts);
}

export async function updateSwapOrder(chainId, signer, index, minOut, triggerRatio, triggerAboveThreshold, opts) {
  const params = [index, minOut, triggerRatio, triggerAboveThreshold];
  const method = "updateSwapOrder";
  const orderBookAddress = getContract(chainId, "OrderBook");
  const contract = new ethers.Contract(orderBookAddress, OrderBook.abi, signer);

  return callContract(chainId, contract, method, params, opts);
}

export async function _executeOrder(chainId, signer, method, account, index, feeReceiver, opts) {
  const params = [account, index, feeReceiver];
  const positionManagerAddress = getContract(chainId, "PositionManager");
  const contract = new ethers.Contract(positionManagerAddress, PositionManager.abi, signer);
  return callContract(chainId, contract, method, params, opts);
}

export function executeSwapOrder(chainId, signer, account, index, feeReceiver, opts) {
  return _executeOrder(chainId, signer, "executeSwapOrder", account, index, feeReceiver, opts);
}

export function executeIncreaseOrder(chainId, signer, account, index, feeReceiver, opts) {
  return _executeOrder(chainId, signer, "executeIncreaseOrder", account, index, feeReceiver, opts);
}

export function executeDecreaseOrder(chainId, signer, account, index, feeReceiver, opts) {
  return _executeOrder(chainId, signer, "executeDecreaseOrder", account, index, feeReceiver, opts);
}

export function usePositionStates(_chainId) {
  return {
    totalLongPositionCollaterals: bigNumberify(0),
    totalLongPositionSizes: bigNumberify(0),
    totalShortPositionCollaterals: bigNumberify(0),
    totalShortPositionSizes: bigNumberify(0),
    totalActivePositions: 0,
  };
}

export function useVolumeData(_params?: { from?: number; to?: number }) {
  const { data } = useSWR(["useVolumeData"], async () => {
    const res = await fetchVolumeStats();
    // totalVolume and dailyVolume are 1e30 BigInt strings from the indexer
    const total = res.totalVolume ? Number(BigInt(res.totalVolume)) / 1e30 : 0;
    const daily = res.dailyVolume ? Number(BigInt(res.dailyVolume)) / 1e30 : 0;
    return [total, daily] as [number, number];
  });
  return data ?? [undefined, undefined];
}